import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Loading } from './components/ui';
import { ActiveLobbyProvider } from './lib/activeLobby';
import { useAuth } from './lib/auth';
import { detectDevice } from './lib/device';

// TV / big screen
const TvHome = lazy(() => import('./pages/tv/TvHome'));
const TvGame = lazy(() => import('./pages/tv/TvGame'));
const TvSession = lazy(() => import('./pages/tv/TvSession'));
const TvLogin = lazy(() => import('./pages/tv/TvLogin'));
// Phone
const MobileHome = lazy(() => import('./pages/mobile/MobileHome'));
const JoinPage = lazy(() => import('./pages/mobile/JoinPage'));
const PlayPage = lazy(() => import('./pages/mobile/PlayPage'));
const LinkDevice = lazy(() => import('./pages/mobile/LinkDevice'));
const StorePage = lazy(() => import('./pages/mobile/StorePage'));
const StoreGamePage = lazy(() => import('./pages/mobile/StoreGamePage'));
const ConnectionTest = lazy(() => import('./pages/ConnectionTest'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
// Account
const LoginPage = lazy(() => import('./pages/account/LoginPage'));
const CheckoutPage = lazy(() => import('./pages/account/CheckoutPage'));
const LibraryPage = lazy(() => import('./pages/account/LibraryPage'));
const ProfilePage = lazy(() => import('./pages/account/ProfilePage'));
const OrdersPage = lazy(() => import('./pages/account/OrdersPage'));
const FriendsPage = lazy(() => import('./pages/account/FriendsPage'));
// Creator
const CreatorHome = lazy(() => import('./pages/creator/CreatorHome'));
const CreatorEditor = lazy(() => import('./pages/creator/CreatorEditor'));
// Admin
const AdminApp = lazy(() => import('./pages/admin/AdminApp'));

function Home() {
  const device = detectDevice();
  return <Navigate to={device === 'mobile' ? '/m' : '/tv'} replace />;
}

export function RequireAuth({ children, admin }: { children: ReactNode; admin?: boolean }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <Loading />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (admin && user.role !== 'admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <ActiveLobbyProvider>
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/" element={<Home />} />

        <Route path="/tv" element={<TvHome />} />
        <Route path="/tv/game/:slug" element={<TvGame />} />
        <Route path="/tv/session/:sessionId" element={<TvSession />} />
        <Route path="/tv/login" element={<TvLogin />} />

        <Route path="/m" element={<MobileHome />} />
        <Route path="/j" element={<JoinPage />} />
        <Route path="/j/:code" element={<JoinPage />} />
        <Route path="/play/:code" element={<PlayPage />} />
        <Route path="/link" element={<RequireAuth><LinkDevice /></RequireAuth>} />
        <Route path="/store" element={<StorePage />} />
        <Route path="/test" element={<ConnectionTest />} />
        <Route path="/gizlilik" element={<PrivacyPage />} />
        <Route path="/privacy" element={<Navigate to="/gizlilik" replace />} />
        <Route path="/store/:slug" element={<StoreGamePage />} />

        <Route path="/login" element={<LoginPage mode="login" />} />
        <Route path="/register" element={<LoginPage mode="register" />} />
        <Route path="/checkout/:purchaseId" element={<RequireAuth><CheckoutPage /></RequireAuth>} />
        <Route path="/library" element={<RequireAuth><LibraryPage /></RequireAuth>} />
        <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
        <Route path="/orders" element={<RequireAuth><OrdersPage /></RequireAuth>} />
        <Route path="/friends" element={<RequireAuth><FriendsPage /></RequireAuth>} />

        <Route path="/creator" element={<RequireAuth><CreatorHome /></RequireAuth>} />
        <Route path="/creator/new/:templateId" element={<RequireAuth><CreatorEditor /></RequireAuth>} />
        <Route path="/creator/edit/:gameId" element={<RequireAuth><CreatorEditor /></RequireAuth>} />

        <Route path="/admin/*" element={<RequireAuth admin><AdminApp /></RequireAuth>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
    </ActiveLobbyProvider>
  );
}
